import React, { useEffect, useRef, useCallback } from 'react';
import { Scene, PointLayer, ILayer } from '@antv/l7';
import { GaodeMap } from '@antv/l7-maps';

interface Map3DProps {
  data?: Array<{ lng: number; lat: number; name?: string; status?: string; [key: string]: any }>;
}

const Map3D: React.FC<Map3DProps> = ({ data = [] }) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<Scene | null>(null);
  const pointLayerRef = useRef<ILayer | null>(null);
  const mapIdRef = useRef(`map3d-${Date.now()}-${Math.random()}`);
  const isSceneLoadedRef = useRef(false);

  const updateLayer = useCallback(() => {
    if (!sceneRef.current || !isSceneLoadedRef.current) return;

    console.log('Map3D updateLayer - data:', data);

    if (!data || data.length === 0) {
      console.log('Map3D: 没有数据');
      return;
    }

    const pointData = data
      .map((item) => {
        const lng = Number(item.lng);
        const lat = Number(item.lat);
        // 验证坐标有效性
        if (isNaN(lng) || isNaN(lat) || lng === 0 || lat === 0) {
          return null;
        }
        return {
          longitude: lng,
          latitude: lat,
          name: item.name || '',
          status: item.status || '',
        };
      })
      .filter((item) => item !== null) as Array<{
        longitude: number;
        latitude: number;
        name: string;
        status: string;
      }>;

    console.log('Map3D pointData:', pointData);

    if (pointLayerRef.current) {
      sceneRef.current.removeLayer(pointLayerRef.current);
      pointLayerRef.current = null;
    }

    if (pointData.length === 0) return;

    // 计算中心点并调整地图视图（仅调整中心，不改变 zoom）
    const lngs = pointData.map((d) => d.longitude).filter((v) => v && !isNaN(v));
    const lats = pointData.map((d) => d.latitude).filter((v) => v && !isNaN(v));
    
    if (lngs.length > 0 && lats.length > 0) {
      const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
      const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
      sceneRef.current.setCenter([centerLng, centerLat]);
      // 不设置 zoom，保持初始化时的 zoom 值
    }

    const pointLayer = new PointLayer({})
      .source(pointData, {
        parser: {
          type: 'json',
          x: 'longitude',
          y: 'latitude',
        },
      })
      .shape('name', ['cylinder'])
      .active(true)
      .size('name', () => {
        return [3, 3, 7];
      })
      .color('status', (status) => {
        // 在线：绿色，离线：灰色
        return status === '离线' ? '#999999' : '#228B22';
      })
      .style({
        opacity: 1,
      });

    pointLayerRef.current = pointLayer;
    sceneRef.current.addLayer(pointLayer);
  }, [data]);

  useEffect(() => {
    if (!mapContainerRef.current) return;

    mapContainerRef.current.id = mapIdRef.current;

    const scene = new Scene({
      id: mapIdRef.current,
      map: new GaodeMap({
        style: 'light',
        center: [121.400257, 31.25287],
        zoom: 3.6,
        pitch: 40,
        token: '3c8ea75ab435da6dbc3194b9b20c53b3',
      }),
    });

    sceneRef.current = scene;

    scene.on('loaded', () => {
      isSceneLoadedRef.current = true;
      updateLayer();
    });

    return () => {
      if (sceneRef.current) {
        sceneRef.current.destroy();
        isSceneLoadedRef.current = false;
      }
    };
  }, [updateLayer]);

  useEffect(() => {
    updateLayer();
  }, [updateLayer]);

  return <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />;
};

export default Map3D;
